import { computed, ref, watch } from 'vue';
import type { SyncState } from '@task-manager/ui';
import { localSync } from '../services/LocalSyncService';

export interface SyncStatusInfo {
  state: SyncState;
  label: string;
  pendingCount: number;
  lastSyncedAt: string | null;
  downloadingProgress: string | null;
}

const activeDownloadProgress = ref<{ completed: number; total: number } | null>(null);
const baseTitle = typeof document !== 'undefined' ? document.title.replace(/^[!●↻]\s+/, '') || 'Geobu' : 'Geobu';

function updateDocumentTitle(prefix: string) {
  if (typeof document === 'undefined') return;
  const cleanBase = document.title.replace(/^[!●↻]\s+/, '') || baseTitle;
  document.title = prefix ? `${prefix}${cleanBase}` : cleanBase;
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (localSync.hasPending()) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
}

export function useSyncStatus() {
  const isOnline = computed(() => localSync.state.connection !== 'offline');
  const pendingActions = computed(() => localSync.state.pending);
  const pendingWords = computed(() => localSync.state.pendingWords);
  const pendingPreferences = computed(() => localSync.state.pendingPreferences);
  const totalWords = computed(() => localSync.state.totalWords);
  const isSyncing = computed(
    () =>
      localSync.state.status === 'syncing' ||
      localSync.state.bootstrapping ||
      Boolean(
        activeDownloadProgress.value &&
          activeDownloadProgress.value.completed < activeDownloadProgress.value.total
      )
  );
  const lastSyncedAt = computed(() =>
    localSync.state.lastSyncedAt
      ? new Date(localSync.state.lastSyncedAt).toISOString()
      : null
  );
  const lastError = computed(() => localSync.state.error || null);

  const presentationState = computed<SyncState>(() => {
    if (localSync.state.connection === 'offline') return 'offline';
    if (
      localSync.state.connection === 'auth_required' ||
      localSync.state.error ||
      localSync.state.failed > 0
    ) {
      return 'error';
    }
    if (isSyncing.value) return 'syncing';
    if (localSync.state.pending > 0 || localSync.state.connection === 'unreachable') {
      return 'pending';
    }
    return 'synced';
  });

  const label = computed(() => {
    if (localSync.state.connection === 'offline') {
      return localSync.state.pending > 0
        ? `Offline — ${localSync.state.pending} pending`
        : 'Offline';
    }
    if (localSync.state.connection === 'auth_required') {
      return 'Sign in required to synchronize';
    }
    if (localSync.state.error) {
      return `Synchronization needs attention`;
    }
    if (localSync.state.bootstrapping) {
      return 'Downloading words for offline use';
    }
    if (
      activeDownloadProgress.value &&
      activeDownloadProgress.value.completed < activeDownloadProgress.value.total
    ) {
      return `Downloading resources (${activeDownloadProgress.value.completed}/${activeDownloadProgress.value.total})`;
    }
    if (localSync.state.status === 'syncing') {
      return 'Synchronizing...';
    }
    if (localSync.state.connection === 'unreachable') {
      return 'Connection trouble — working offline';
    }
    if (localSync.state.pending > 0) {
      return `${localSync.state.pending} ${
        localSync.state.pending === 1 ? 'change' : 'changes'
      } waiting to synchronize`;
    }
    return 'Synchronized';
  });

  watch(
    () => [
      presentationState.value,
      localSync.state.connection,
      localSync.state.realtime,
      localSync.state.pending,
    ],
    () => {
      if (
        localSync.state.connection === 'auth_required' ||
        presentationState.value === 'error'
      ) {
        updateDocumentTitle('! ');
      } else if (
        localSync.state.connection === 'offline' ||
        localSync.state.connection === 'unreachable' ||
        localSync.state.pending > 0
      ) {
        updateDocumentTitle('● ');
      } else if (presentationState.value === 'syncing') {
        updateDocumentTitle('↻ ');
      } else {
        updateDocumentTitle('');
      }
    },
    { immediate: true }
  );

  const setDownloadProgress = (completed: number, total: number) => {
    if (completed >= total) {
      activeDownloadProgress.value = null;
      localSync.state.lastSyncedAt = Date.now();
    } else {
      activeDownloadProgress.value = { completed, total };
    }
  };

  const markSyncSuccess = () => {
    localSync.state.error = '';
    if (typeof navigator === 'undefined' || navigator.onLine) {
      localSync.state.connection = 'online';
    }
    localSync.state.lastSyncedAt = Date.now();
  };

  const markSyncError = (err: string) => {
    localSync.state.error = err;
  };

  const retrySync = async () => {
    await localSync.syncNow();
  };

  return {
    presentationState,
    label,
    isOnline,
    pendingActions,
    pendingWords,
    pendingPreferences,
    totalWords,
    isSyncing,
    lastSyncedAt,
    lastError,
    syncState: localSync.state,
    setDownloadProgress,
    markSyncSuccess,
    markSyncError,
    retrySync,
  };
}
